import Dexie, { type EntityTable } from 'dexie';
import type {
  AuthSession,
  BackgroundPreferences,
  MonthFocus,
  Plan,
  Profile,
  Space,
  SpaceInvite,
  SpaceMember,
  SyncOperation,
  UserPreferences,
} from '@/domain/types';

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface BackgroundBlobRow {
  id: string;
  blob: Blob;
  mimeType: string;
  createdAt: string;
}

export interface DayMediaRow {
  id: string;
  spaceId: string;
  dateKey: string;
  ownerId: string;
  mimeType: string;
  fileName: string;
  storagePath: string | null;
  blob: Blob | null;
  createdAt: string;
}

export class TwymeDatabase extends Dexie {
  plans!: EntityTable<Plan, 'id'>;
  monthFocus!: EntityTable<MonthFocus, 'id'>;
  profiles!: EntityTable<Profile, 'id'>;
  spaces!: EntityTable<Space, 'id'>;
  spaceMembers!: EntityTable<SpaceMember, 'userId'>;
  spaceInvites!: EntityTable<SpaceInvite, 'id'>;
  preferences!: EntityTable<UserPreferences, 'userId'>;
  syncQueue!: EntityTable<SyncOperation, 'id'>;
  backgrounds!: EntityTable<BackgroundBlobRow, 'id'>;
  dayMedia!: EntityTable<DayMediaRow, 'id'>;
  meta!: EntityTable<MetaRow, 'key'>;

  constructor(name = 'twyme') {
    super(name);
    this.version(1).stores({
      plans: 'id, spaceId, ownerId, planDate, updatedAt, deletedAt, clientId',
      monthFocus: 'id, spaceId, ownerId, yearMonth, updatedAt',
      profiles: 'id',
      spaces: 'id, createdBy',
      spaceMembers: '[spaceId+userId], spaceId, userId, memberStatus',
      spaceInvites: 'id, spaceId, codeHash, status',
      preferences: 'userId',
      syncQueue: 'id, entityType, createdAt',
      backgrounds: 'id',
      meta: 'key',
    });
    this.version(2)
      .stores({
        plans: 'id, spaceId, ownerId, planDate, updatedAt, deletedAt, clientId',
        monthFocus: 'id, spaceId, ownerId, yearMonth, updatedAt',
        profiles: 'id',
        spaces: 'id, createdBy',
        spaceMembers: '[spaceId+userId], spaceId, userId, memberStatus',
        spaceInvites: 'id, spaceId, codeHash, status',
        preferences: 'userId',
        syncQueue: 'id, entityType, entityId, createdAt',
        backgrounds: 'id',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        const plans = await tx.table('plans').toArray();
        for (const plan of plans) {
          const row = plan as Plan & { colorKey?: string | null };
          if (row.colorKey === undefined) {
            await tx.table('plans').put({ ...row, colorKey: null });
          }
        }
        const queue = await tx.table('syncQueue').toArray();
        for (const op of queue) {
          const row = op as SyncOperation;
          await tx.table('syncQueue').put({
            ...row,
            lastErrorDetail: row.lastErrorDetail ?? null,
            errorKind: row.errorKind ?? null,
            nextRetryAt: row.nextRetryAt ?? null,
          });
        }
      });
    this.version(3).stores({
      dayMedia: 'id, spaceId, dateKey, ownerId, [spaceId+dateKey]',
    });
  }
}

let dbInstance: TwymeDatabase | null = null;

export function getDb(): TwymeDatabase {
  if (!dbInstance) {
    dbInstance = new TwymeDatabase();
  }
  return dbInstance;
}

export async function clearPrivateCache(): Promise<void> {
  const db = getDb();
  await Promise.all([
    db.plans.clear(),
    db.monthFocus.clear(),
    db.profiles.clear(),
    db.spaces.clear(),
    db.spaceMembers.clear(),
    db.spaceInvites.clear(),
    db.preferences.clear(),
    db.syncQueue.clear(),
    db.backgrounds.clear(),
    db.dayMedia.clear(),
    db.meta.clear(),
  ]);
}

export async function getMeta<T>(key: string): Promise<T | null> {
  const row = await getDb().meta.get(key);
  return (row?.value as T) ?? null;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await getDb().meta.put({ key, value });
}

export async function saveSession(session: AuthSession | null): Promise<void> {
  if (!session) {
    await getDb().meta.delete('session');
    return;
  }
  await setMeta('session', session);
}

export async function loadSession(): Promise<AuthSession | null> {
  return getMeta<AuthSession>('session');
}

export const DEFAULT_BACKGROUND: BackgroundPreferences = {
  imageId: null,
  storagePath: null,
  source: 'none',
  fit: 'cover',
  positionX: 50,
  positionY: 50,
  dim: 0.55,
  blur: 0,
  surfaceOpacity: 0.92,
};
